import React, { useContext } from 'react';
import athlete_skeleton from './athlete_skeleton.png';
import { motion } from 'framer-motion';
import { CountryFlag } from '../../Utils/flags';
import { convertTime } from '../HeadToHead/utils';
import { secondsInPast } from '../../Utils/time';
import { AppContext } from '../../App';
import { List as Advertisement } from '../Advertisement/list';
import { getTrans } from '../../Utils/translate';
import { getCrosslinkClass, handleClickAthleteName } from '../../Utils/crosslink';
import { CosmosImage } from '../../cosmos/web-scoped/react';

const Athlete = ({
  athlete,
  index,
  listType,
  started,
  athlete_id,
  status,
  result,
  delaySeconds,
  lap,
  pagination,
  result_changes,
  start,
  useDifficulty,
  showAthleteImage = true,
  crosslinkDetails,
  dive_difficulty,
  animate,
  advertising
}) => {
  const { first_name, last_name, nationality } = athlete;
  const { iso3 } = nationality;
  let startDate, resultDate;
  let inPast = false;
  let rank = result ? result.rank : undefined;
  let timeToFirst = result ? result.time_to_first : undefined;
  let finishing_status = result ? result.finishing_status : undefined;
  let time = result ? result.time : undefined;

  const context = useContext(AppContext);
  const lang = context.lang;

  if (result_changes) {
    //setRank
    const rankChange = result_changes.filter((change) => change.type === 'rank')[0];
    if (rankChange) {
      rank = secondsInPast(new Date(rankChange.updated), delaySeconds || 60)
        ? rankChange.current
        : rankChange.previous;
      rank = parseInt(rank, 10);
    }

    //set timeToFirst
    const timeToFirstChange = result_changes.filter(
      (change) => change.type === 'time_to_first'
    )[0];
    if (timeToFirstChange) {
      timeToFirst = secondsInPast(new Date(timeToFirstChange.updated), delaySeconds || 60)
        ? timeToFirstChange.current
        : timeToFirstChange.previous;
    }
  }
  if (listType === 'start' && started) {
    startDate = new Date(started);
    inPast = secondsInPast(startDate, delaySeconds || 60);
  }

  if ((listType === 'result' || listType === 'lap' || listType === 'points') && result) {
    resultDate = new Date(result.result_time ? result.result_time : result);
    inPast = secondsInPast(resultDate, delaySeconds || 60);
  }

  const getResult = () => {
    if (listType === 'result') {
      if (finishing_status !== 'finished' && status !== 'ontrack') {
        return finishing_status;
      } else if (finishing_status === 'finished') {
        if (rank !== 1) {
          let time = convertTime(timeToFirst, 3);

          //hide zeros
          if (time && time.startsWith('0:0')) {
            time = time.slice(3);
          }
          if (time && time.startsWith('0:')) {
            time = time.slice(2);
          }
          return `+ ${time}`;
          //hide zeros
        }
        // return `+ ${time}`;
      } else if (timeToFirst && rank > 1) {
        let time = convertTime(timeToFirst, 0);
        //hide zeros
        if (time && time.startsWith('0:0')) {
          time = time.slice(3);
        }
        if (time && time.startsWith('0:')) {
          time = time.slice(2);
        }
        return `+ ${time}`;
      }
      return convertTime(time, 3);
    } else if (useDifficulty) {
      return parseFloat(dive_difficulty).toFixed(1);
    } else if (listType === 'start') {
      if (
        finishing_status &&
        inPast &&
        !['finished', 'unknown'].includes(finishing_status.toLowerCase())
      ) {
        return `${finishing_status.toUpperCase()}`;
      } else if (
        finishing_status &&
        !['finished', 'unknown'].includes(finishing_status.toLowerCase())
      ) {
        return `${finishing_status.toUpperCase()}`;
      }
      return `# ${athlete_id}`;
    } else if (listType === 'lap') {
      if (timeToFirst && rank > 1) {
        let time = convertTime(timeToFirst, 0);

        //hide zeros
        if (time.startsWith('0:0')) {
          time = time.slice(3);
        }
        if (time.startsWith('0:')) {
          time = time.slice(2);
        }
      }
      return convertTime(time, 1);
    } else if (listType === 'points') {
      if (finishing_status !== 'finished' && status !== 'ontrack') {
        return finishing_status;
      } else {
        return `${result.total_points}`
      }
    }
  };

  const getTextClass = (color = 'white', timing = false) => {
    if (timing && listType !== 'start') {
      return index < 1 ? ` text-${color}` : '';
    }
    return index < 3 && (listType === 'result' || listType === 'lap' || listType === 'points')
      ? ` text-${color}`
      : listType === 'start' &&
        useDifficulty &&
        (status === 'on_track' || status === 'ontrack')
      ? ` text-blue`
      : !started && !inPast && listType === 'start' && status !== 'result' && status !== 'points'
      ? ` text-${color}`
      : '';
  };

  return (
    <>
      {(listType === 'start' || inPast) && (
        <>
          <motion.tr
            className={`athlete${index < 3 ? ' text-white' : ''} ${index % 2 === 0 ? 'odd' : 'even'}`}
            positionTransition
            initial={{
              opacity: 0,
              height: animate && listType !== 'start' ? 54 : 64
            }}
            transition={{
              opacity: {
                duration: animate && listType !== 'start' ? 3 : 0.5,
                delay: animate && listType !== 'start' ? 3.5 : 0
              },
              height: {
                duration: 1,
                delay: animate && listType !== 'start' ? 2 : 0
              }
            }}
            animate={{ opacity: 1, height: 64 }}
            key={last_name}>
            <td
              className={`rank font-mono text-right${getTextClass(
                listType === 'result' || listType === 'lap' || listType === 'points' ? 'red' : 'white'
              )}`}>
              {listType === 'start' ? start.order : rank !== 0 ? rank : ' '}.
            </td>
            {showAthleteImage && (
              <td
                onClick={(e) => { handleClickAthleteName(e, athlete.id, crosslinkDetails); }}
                className={`athleteImage ${getCrosslinkClass(context, crosslinkDetails)}`}>
                <div>
                  <CosmosImage
                    image={athlete.image ? (athlete.image.profile || athlete_skeleton) : athlete_skeleton}
                    image-preview={athlete_skeleton}
                    image-alt={`image-${athlete.first_name}-${athlete.last_name}`}/>
                  </div>
              </td>
            )}
            <td
              onClick={(e) => { handleClickAthleteName(e, athlete.id, crosslinkDetails); }}
              className={`flag-td ${getCrosslinkClass(context, crosslinkDetails)}`}>
              <img className="flag" src={CountryFlag(iso3)} alt={iso3} />
            </td>
            <td
              onClick={(e) => { handleClickAthleteName(e, athlete.id, crosslinkDetails); }}
              className={`name ${
                listType === 'lap' ? 'nameSmall ' : ''
              }${getTextClass()} ${getCrosslinkClass(context, crosslinkDetails)}`}>
              {first_name && first_name.toLowerCase()}{' '}
              {last_name && last_name.toLowerCase()}
              {athlete.wildcard === true && (
                <>
                  {' '}
                  <span style={{ fontSize: "0.9rem" }}>(WC)</span>
                </>
              )}
            </td>
            {listType === 'lap' && !pagination ? (
              <td className={`lap`}>
                {getTrans(lang.lap)} <span className={`font-mono`}>{lap}</span>
              </td>
            ) : undefined}
            <td className={`time font-mono ${getTextClass('white', true)}`}>
              {getResult()}
            </td>
          </motion.tr>
          {(listType !== 'start' || listType === 'result' || listType === 'points') && (
            <Advertisement advertising={advertising} show={animate} />
          )}
        </>
      )}
    </>
  );
};

export { Athlete };
